import React from 'react';
import { Modal } from 'antd';
import { BuildingsIcon, SunIcon } from '@phosphor-icons/react';

export type DashboardLayoutRoofDetailsModalProps = {
    theme: 'dark' | 'light';
    open: boolean;
    onClose: () => void;
    title: string;
    simulatingLayouts: boolean;
    layoutProductionByKey: Record<string, number | undefined>;
    selectedRoofCombinationKey: string;
    totalModulesBestRoofs: number | null | undefined;
    activeLayoutRoofIds: string[];
    activeLayoutOverUsage: boolean;
    activeLayoutProduction: number;
    annualUsageCapKwh: number | null | undefined;
    activeModule: unknown;
    roofCards: any[];
    expectedCountByRoofId: Record<string, number | undefined>;
    treeCount: number;
};

function formatSystemKw(mods: number | null | undefined, activeModule: unknown): string {
    const pw = (activeModule as any)?.power ?? 0;
    return mods != null && pw > 0 ? ((mods * pw) / 1000).toFixed(2) : '—';
}

function formatEffPercent(mods: number | null | undefined, prod: number): string {
    return mods && prod ? `${Math.round(prod / mods)} %` : '—';
}

function formatSolarAccessAvg(roofCards: any[], activeLayoutRoofIds: string[]): string {
    if (!roofCards?.length || !activeLayoutRoofIds?.length) return '—';
    const ids = new Set(activeLayoutRoofIds);
    const relevant = roofCards.filter((r: any) => ids.has(r.roofFaceId) && typeof r.solarAccess === 'number');
    if (!relevant.length) return '—';
    const avg = relevant.reduce((sum: number, r: any) => sum + (r.solarAccess as number), 0) / relevant.length;
    return `${avg.toFixed(1)}%`;
}

export const DashboardLayoutRoofDetailsModal: React.FC<DashboardLayoutRoofDetailsModalProps> = ({
    theme,
    open,
    onClose,
    title,
    simulatingLayouts,
    layoutProductionByKey,
    selectedRoofCombinationKey,
    totalModulesBestRoofs,
    activeLayoutRoofIds,
    activeLayoutOverUsage,
    activeLayoutProduction,
    annualUsageCapKwh,
    activeModule,
    roofCards,
    expectedCountByRoofId,
    treeCount,
}) => {
    const systemKw = formatSystemKw(totalModulesBestRoofs ?? null, activeModule);
    const effLabel = formatEffPercent(totalModulesBestRoofs ?? null, activeLayoutProduction);
    const solarAvg = formatSolarAccessAvg(roofCards, activeLayoutRoofIds);

    return (
        <Modal
            title={title}
            open={open}
            onCancel={onClose}
            footer={null}
            width={520}
            className={`editor-ai-details-modal obd-theme-${theme}`}
            rootClassName={`obd-form-controls-modal-root obd-theme-${theme}`}
            destroyOnHidden
        >
            <div className="editor-ai-layout-detail-card">
                <div className="editor-ai-layout-detail-card__head">
                    <div className="editor-ai-layout-detail-card__totals">
                        <span>
                            Modules: <strong>{totalModulesBestRoofs ?? '—'}</strong>
                        </span>
                        <span>
                            Roofs: <strong>{activeLayoutRoofIds?.length ?? '—'}</strong>
                        </span>
                        <span>
                            Production:{' '}
                            <strong
                                className={activeLayoutOverUsage ? 'editor-ai-layout-detail__metric--over-usage' : undefined}
                            >
                                {simulatingLayouts && !layoutProductionByKey[selectedRoofCombinationKey]
                                    ? '…'
                                    : Math.round(activeLayoutProduction).toLocaleString()}{' '}
                                kWh/yr
                            </strong>
                            {annualUsageCapKwh != null && activeLayoutOverUsage && (
                                <span className="editor-ai-layout-detail__usage-hint">
                                    {' '}
                                    (cap {Math.round(annualUsageCapKwh).toLocaleString()} kWh/yr)
                                </span>
                            )}
                        </span>
                        <span>
                            System: <strong>{systemKw} kW</strong>
                        </span>
                        <span>
                            Eff.: <strong>{effLabel}</strong>
                        </span>
                        <span>
                            Solar access: <strong>{solarAvg}</strong>
                        </span>
                        <span>
                            Trees: <strong>{treeCount}</strong>
                        </span>
                    </div>
                </div>
                <div className="editor-ai-layout-detail-card__roofs" style={{ maxHeight: 360, overflowY: 'auto' }}>
                    {roofCards.map((r: any, idx: number) => {
                        const isBest = activeLayoutRoofIds.includes(r.roofFaceId);
                        const displayModuleCount =
                            isBest && expectedCountByRoofId[r.roofFaceId] != null
                                ? expectedCountByRoofId[r.roofFaceId]
                                : (r.moduleCount ?? 0);
                        return (
                            <div
                                key={r.roofFaceId ?? idx}
                                className={`editor-ai-roof-row ${isBest ? 'editor-ai-roof-row--in-layout' : ''}`}
                            >
                                <div className="editor-ai-roof-row__head">
                                    <BuildingsIcon size={16} weight="duotone" className="editor-ai-roof-row__icon" />
                                    <span className="editor-ai-roof-row__name">Roof {r.roofNum}</span>
                                    {isBest && (
                                        <span className="editor-ai-roof-row__badge">
                                            <SunIcon size={12} weight="fill" /> In selected layout
                                        </span>
                                    )}
                                </div>
                                <div className="editor-ai-roof-row__meta">
                                    <span>
                                        Modules: <strong>{displayModuleCount}</strong>
                                        {isBest &&
                                            expectedCountByRoofId[r.roofFaceId] != null &&
                                            expectedCountByRoofId[r.roofFaceId] !== r.moduleCount && (
                                                <span className="editor-ai-roof-row__hint"> (when applied)</span>
                                            )}
                                    </span>
                                    <span>
                                        Solar access:{' '}
                                        <strong>{typeof r.solarAccess === 'number' ? r.solarAccess.toFixed(1) : '—'}%</strong>
                                    </span>
                                    <span>
                                        Multiplier:{' '}
                                        <strong>
                                            {typeof r.roofMultiplier === 'number' ? r.roofMultiplier.toFixed(4) : '—'}
                                        </strong>
                                    </span>
                                    <span>
                                        Est. production:{' '}
                                        <strong>
                                            {r.productionIsExact ? '' : '~ '}
                                            {r.productionApprox.toLocaleString()} W
                                        </strong>
                                    </span>
                                    {r.pointCount > 0 && (
                                        <span>
                                            Shade points: <strong>{r.pointCount}</strong> (avg ptHash: {r.avgPtHash})
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Modal>
    );
};
